import { Priority, VisualState, useKBar, useRegisterActions } from "kbar";
import { type PropsWithChildren, useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { jt, t } from "ttag";

import { useListRecentsQuery, useSearchQuery } from "metabase/api";
import { ROOT_COLLECTION } from "metabase/common/collections/constants";
import { getCollection } from "metabase/common/collections/utils";
import type { OmniPickerItem } from "metabase/common/components/Pickers";
import { trackSearchClick } from "metabase/common/search/analytics";
import {
  canAccessSettings,
  getUserIsAdmin,
  isSettingsManagerPath,
} from "metabase/current-user";
import { useGetIcon } from "metabase/hooks/use-icon";
import { useSelector } from "metabase/redux";
import { queryToSearch } from "metabase/router";
import { getDocsUrl } from "metabase/selectors/settings";
import { getShowMetabaseLinks } from "metabase/selectors/whitelabel";
import { getSettings, useSetting } from "metabase/settings";
import { Icon, Text } from "metabase/ui";
import * as Urls from "metabase/urls";
import { modelToUrl } from "metabase/urls";
import type { SearchQuery } from "metabase/utils/browser";
import { SEARCH_DEBOUNCE_DURATION } from "metabase/utils/constants";
import { getName } from "metabase/utils/name";
import {
  type RecentCollectionItem,
  type RecentItem,
  isRecentTableItem,
} from "metabase-types/api";

import { METABASE_DOCS_LABELS, getAdminSettingsSections } from "../constants";
import type { PaletteAction } from "../types";
import { filterRecentItems } from "../utils";

export const useCommandPalette = ({
  disabled = false,
  locationQuery,
}: {
  disabled: boolean;
  locationQuery: SearchQuery;
}) => {
  const getIcon = useGetIcon();
  const showMetabaseLinks = useSelector(getShowMetabaseLinks);
  const { isVisible } = useKBar((s) => ({
    isVisible: s.visualState !== VisualState.hidden,
  }));

  const isAdmin = useSelector(getUserIsAdmin);
  const canUserAccessSettings = useSelector(canAccessSettings);

  const isSearchTypeaheadEnabled = useSetting("search-typeahead-enabled");

  // Used for finding actions within the list
  const { searchQuery } = useKBar((state) => ({
    searchQuery: state.searchQuery,
  }));
  const trimmedQuery = searchQuery.trim();

  // Used for finding objects across the Metabase instance
  const [debouncedSearchText, setDebouncedSearchText] = useState(trimmedQuery);

  useDebounce(
    () => {
      setDebouncedSearchText(isVisible ? trimmedQuery : "");
    },
    isVisible ? SEARCH_DEBOUNCE_DURATION : 0,
    [trimmedQuery, isVisible],
  );

  const hasQuery = searchQuery.length > 0;

  const docsUrl = useSelector((state) => getDocsUrl(state, {}));
  const docsSearchUrl = useSelector((state) =>
    debouncedSearchText
      ? getDocsUrl(state, {
          searchQuery: debouncedSearchText,
          utm: {
            utm_medium: "command-palette",
            utm_campaign: "docs-search",
          },
        })
      : null,
  );

  const {
    currentData: searchResults,
    isFetching: isSearchLoading,
    error: searchError,
    requestId: searchRequestId,
  } = useSearchQuery(
    {
      q: debouncedSearchText,
      context: "command-palette",
      include_dashboard_questions: true,
      limit: 20,
    },
    {
      skip: !debouncedSearchText || !isSearchTypeaheadEnabled || disabled,
      refetchOnMountOrArgChange: true,
    },
  );

  const { data: recentItems, refetch: refetchRecents } = useListRecentsQuery(
    undefined,
    { skip: disabled },
  );
  useEffect(() => {
    if (isVisible && !disabled) {
      refetchRecents();
    }
  }, [isVisible, refetchRecents, disabled]);

  const settingValues = useSelector(getSettings);

  const docsAction = useMemo<PaletteAction[]>(() => {
    const link = debouncedSearchText ? docsSearchUrl : docsUrl;
    const ret: PaletteAction[] = [
      {
        id: "search_docs",
        name: debouncedSearchText
          ? METABASE_DOCS_LABELS.searchLabel(debouncedSearchText)
          : METABASE_DOCS_LABELS.viewLabel,
        section: "docs",
        // Include search query in keywords so kbar always shows it
        keywords: trimmedQuery,
        icon: "document",
        extra: {
          href: link,
        },
      },
    ];
    return ret;
  }, [debouncedSearchText, docsSearchUrl, docsUrl, trimmedQuery]);

  const showDocsAction = showMetabaseLinks && hasQuery && !disabled;

  useRegisterActions(showDocsAction ? docsAction : [], [
    docsAction,
    showDocsAction,
  ]);

  const searchResultActions = useMemo<PaletteAction[]>(() => {
    if (disabled) {
      return [];
    }

    const searchLocation = {
      pathname: "search",
      search: queryToSearch({
        ...locationQuery,
        q: debouncedSearchText,
      }),
    };
    if (!isSearchTypeaheadEnabled) {
      return [
        {
          id: `search-without-typeahead`,
          name: t`View search results for "${debouncedSearchText}"`,
          section: "search",
          keywords: trimmedQuery,
          icon: "link" as const,
          priority: Priority.HIGH,
          extra: {
            href: searchLocation,
          },
        },
      ];
    } else if (isSearchLoading) {
      return [
        {
          id: "search-is-loading",
          name: t`Loading...`,
          keywords: trimmedQuery,
          section: "search",
          disabled: true,
        },
      ];
    } else if (searchError) {
      return [
        {
          id: "search-error",
          name: t`Could not load search results`,
          section: "search",
          disabled: true,
        },
      ];
    } else if (debouncedSearchText) {
      if (searchResults?.data.length) {
        return searchResults.data.map((result, index) => {
          const icon = getIcon(result);
          return {
            id: `search-result-${result.model}-${result.id}`,
            name: result.name,
            subtitle: result.description || "",
            icon: icon.name,
            iconUrl: icon.iconUrl,
            section: "search",
            keywords: trimmedQuery,
            priority: Priority.NORMAL - index,
            perform: () => {
              trackSearchClick({
                itemType: "item",
                position: index,
                context: "command-palette",
                searchEngine: searchResults?.engine || "unknown",
                requestId: searchRequestId,
                entityModel: result.model,
                entityId: typeof result.id === "number" ? result.id : null,
                searchTerm: debouncedSearchText,
              });
            },
            extra: {
              moderatedStatus: result.moderated_status,
              href: modelToUrl(result),
              iconColor: icon.color,
              subtext: getSearchResultSubtext(result),
            },
          };
        });
      } else {
        return [
          {
            id: "no-search-results",
            name: t`No results for “${debouncedSearchText}”`,
            keywords: trimmedQuery,
            section: "search",
            disabled: true,
          },
        ];
      }
    }
    return [];
  }, [
    disabled,
    debouncedSearchText,
    trimmedQuery,
    isSearchLoading,
    searchError,
    searchResults,
    locationQuery,
    isSearchTypeaheadEnabled,
    searchRequestId,
    getIcon,
  ]);

  useRegisterActions(searchResultActions, [searchResultActions]);

  const recentItemsActions = useMemo<PaletteAction[]>(() => {
    if (disabled) {
      return [];
    }

    return (
      filterRecentItems(recentItems ?? []).map((item) => {
        const icon = getIcon(item);
        return {
          id: `recent-item-${getName(item)}-${item.model}-${item.id}`,
          name: getName(item),
          icon: icon.name,
          iconUrl: icon.iconUrl,
          section: "recent",
          perform: () => {},
          extra: {
            moderatedStatus: isRecentCollectionItem(item)
              ? item.moderated_status
              : null,
            href: Urls.modelToUrl(item),
            iconColor: icon.color,
            subtext: getRecentItemSubtext(item),
          },
        };
      }) || []
    );
  }, [disabled, recentItems, getIcon]);

  useRegisterActions(hasQuery ? [] : recentItemsActions, [
    recentItemsActions,
    hasQuery,
  ]);

  const settingsActions = useMemo<PaletteAction[]>(() => {
    if (disabled || !canUserAccessSettings) {
      return [];
    }

    return Object.entries(getAdminSettingsSections(settingValues))
      .filter(([slug, section]) => {
        if (section.hidden) {
          return false;
        }

        if (!isAdmin && !isSettingsManagerPath(slug)) {
          return false;
        }

        return true;
      })
      .map(([slug, section]) => ({
        id: `admin-settings-${slug}`,
        name: `${t`Settings`} - ${section.name}`,
        icon: "gear",
        perform: () => {},
        section: "admin",
        extra: {
          href: `/admin/settings/${slug}`,
        },
      }));
  }, [disabled, canUserAccessSettings, isAdmin, settingValues]);

  useRegisterActions(hasQuery ? settingsActions : [], [
    settingsActions,
    hasQuery,
  ]);

  return {
    searchRequestId,
    searchResults,
    liveSearchTerm: trimmedQuery,
    debouncedSearchTerm: debouncedSearchText,
  };
};

export const getSearchResultSubtext = (wrappedSearchResult: any) => {
  if (wrappedSearchResult.model === "indexed-entity") {
    return (
      <SubtitleText>{jt`a record in ${(
        <Icon
          flex="0 0 auto"
          key="icon"
          name="model"
          style={{
            verticalAlign: "bottom",
            marginInlineStart: "0.25rem",
          }}
        />
      )} ${wrappedSearchResult.model_name}`}</SubtitleText>
    );
  } else if (wrappedSearchResult.model === "table") {
    return wrappedSearchResult.collection?.name ? (
      <SubtitleText>{wrappedSearchResult.collection.name}</SubtitleText>
    ) : (
      <SubtitleText>
        {wrappedSearchResult.table_schema
          ? `${wrappedSearchResult.database_name} (${wrappedSearchResult.table_schema})`
          : wrappedSearchResult.database_name}
      </SubtitleText>
    );
  } else if (
    wrappedSearchResult.model === "card" &&
    wrappedSearchResult.dashboard
  ) {
    return (
      <>
        <Icon
          flex="0 0 auto"
          name="dashboard"
          style={{
            verticalAlign: "bottom",
            marginInline: "0.25rem",
          }}
        />
        <SubtitleText>{wrappedSearchResult.dashboard.name}</SubtitleText>
      </>
    );
  } else {
    return (
      <SubtitleText>{getCollection(wrappedSearchResult)?.name}</SubtitleText>
    );
  }
};

export const getRecentItemSubtext = (item: RecentItem) => {
  if (isRecentTableItem(item)) {
    return (
      <SubtitleText>
        {item.table_schema
          ? `${item.database.name} (${item.table_schema})`
          : item.database.name}
      </SubtitleText>
    );
  } else if (item.dashboard) {
    return (
      <>
        <Icon flex="0 0 auto" name="dashboard" size={12} />
        <SubtitleText>{item.dashboard.name}</SubtitleText>
      </>
    );
  } else if (item.parent_collection.id === null) {
    return (
      <>
        <Icon flex="0 0 auto" name="collection" size={12} />
        <SubtitleText>{ROOT_COLLECTION.name}</SubtitleText>
      </>
    );
  } else {
    return (
      <>
        <Icon flex="0 0 auto" name="collection" size={12} />
        <SubtitleText>{item.parent_collection.name}</SubtitleText>
      </>
    );
  }
};

const SubtitleText = ({ children }: PropsWithChildren) => (
  <Text
    lineClamp={1}
    fz="inherit"
    lh="inherit"
    c="inherit"
    style={{ lineBreak: "anywhere" }}
  >
    {children}
  </Text>
);

const isRecentCollectionItem = (
  item: OmniPickerItem,
): item is RecentCollectionItem =>
  ["collection", "dashboard", "card", "dataset", "metric"].includes(item.model);
